const STORAGE_KEY = "cv2.gridConfig";
const SCHEMA_VERSION = 1;

type GridColumnConfig = Record<string, { visible: boolean }>;
type GridConfig = { grid?: GridColumnConfig; filters?: Record<string, unknown> };

type GridConfigPreferences = {
	schemaVersion: number;
	pages: {
		bookings?: {
			daySheet?: GridConfig;
			allBookings?: GridConfig;
			unconfirmedBookings?: GridConfig;
		};
		personnelAndFleet?: {
			personnel?: GridConfig;
			fleet?: GridConfig;
		};
	};
};

const emptyConfig = (): GridConfigPreferences => ({
	schemaVersion: SCHEMA_VERSION,
	pages: {}
});

export function loadGridConfig(): GridConfigPreferences {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return emptyConfig();
		}
		const parsed = JSON.parse(raw) as GridConfigPreferences;
		if (parsed.schemaVersion !== SCHEMA_VERSION) {
			return emptyConfig();
		}
		return parsed;
	} catch {
		return emptyConfig();
	}
}

export function saveGridConfig(config: GridConfigPreferences): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
	} catch {
		// Silently ignore storage errors (e.g. private browsing quota)
	}
}

/**
 * How a column behaves when the user has not chosen anything for it.
 * - `defaultVisible: false` — hidden for a user with no saved column choices for the grid.
 *   Omitted means visible.
 * - `isNew` — added after users may already have saved choices. A user who already has saved
 *   choices for the grid does not see it until they opt in, so adding a column never changes
 *   what an existing user sees.
 */
export type ColumnDefaults = { defaultVisible?: boolean; isNew?: boolean };

/**
 * Resolves visible column ids from stored config.
 * - alwaysVisible columns are always included.
 * - A grid with no saved config shows each column's default (visible unless `defaultVisible: false`).
 * - A grid with saved config honours a column's saved entry. A column with no entry is visible,
 *   as it always has been, unless it is `isNew`, which stays hidden until the user opts in.
 */
export function getColumnVisibilityFromConfig(
	config: GridConfigPreferences,
	path: string[],
	allColumnIds: string[],
	alwaysVisibleIds: Set<string> = new Set(),
	columnDefaults: Record<string, ColumnDefaults> = {}
): Set<string> {
	const gridConfig = resolvePath(config.pages, path) as GridColumnConfig | undefined;

	return new Set(
		allColumnIds.filter((id) => {
			if (alwaysVisibleIds.has(id)) {
				return true;
			}
			if (!gridConfig) {
				return columnDefaults[id]?.defaultVisible ?? true;
			}
			if (id in gridConfig) {
				return gridConfig[id].visible;
			}
			return !columnDefaults[id]?.isNew;
		})
	);
}

/**
 * Returns a new config with the column visibility stored at the given path.
 * Hidden columns are written, and so are visible `isNew` columns — without an explicit entry an
 * `isNew` column would read back as hidden. Other visible columns are omitted.
 */
export function updateColumnVisibilityInConfig(
	config: GridConfigPreferences,
	path: string[],
	visibleIds: Set<string>,
	allColumnIds: string[],
	newColumnIds: Set<string> = new Set()
): GridConfigPreferences {
	const entries: GridColumnConfig = {};
	for (const id of allColumnIds) {
		if (!visibleIds.has(id)) {
			entries[id] = { visible: false };
		} else if (newColumnIds.has(id)) {
			entries[id] = { visible: true };
		}
	}

	const updatedPages = setPath(config.pages, path, entries);
	return { ...config, pages: updatedPages };
}

/**
 * Returns the stored filter values for the given grid path, or undefined if none saved.
 */
export function getFiltersFromConfig<T extends Record<string, unknown>>(
	config: GridConfigPreferences,
	path: string[]
): T | undefined {
	const gridConfig = resolvePath(config.pages, path) as GridConfig | undefined;
	return gridConfig?.filters as T | undefined;
}

/**
 * Removes all saved filter values, preserving column visibility preferences.
 * Call on sign-out so filters don't leak to the next user on a shared machine.
 */
export function clearSavedFilters(): void {
	const config = loadGridConfig();
	const cleared = clearFiltersInPages(config.pages);
	saveGridConfig({ ...config, pages: cleared });
}

function clearFiltersInPages(pages: GridConfigPreferences["pages"]): GridConfigPreferences["pages"] {
	const result: GridConfigPreferences["pages"] = {};
	for (const [pageKey, page] of Object.entries(pages) as [string, Record<string, GridConfig>][]) {
		const clearedPage: Record<string, GridConfig> = {};
		for (const [gridKey, grid] of Object.entries(page)) {
			const { filters: _, ...rest } = grid;
			clearedPage[gridKey] = rest;
		}
		(result as Record<string, unknown>)[pageKey] = clearedPage;
	}
	return result;
}

/**
 * Returns a new config with the filter values stored at the given path.
 */
export function updateFiltersInConfig<T extends Record<string, unknown>>(
	config: GridConfigPreferences,
	path: string[],
	filters: T
): GridConfigPreferences {
	const existing = (resolvePath(config.pages, path) as GridConfig) ?? {};
	return { ...config, pages: setPath(config.pages, path, { ...existing, filters }) };
}

// --- Path helpers ---

function resolvePath(obj: unknown, path: string[]): unknown {
	let current = obj;
	for (const key of path) {
		if (current == null || typeof current !== "object") {
			return undefined;
		}
		current = (current as Record<string, unknown>)[key];
	}
	return current;
}

function setPath<T extends object>(obj: T, path: string[], value: unknown): T {
	if (path.length === 0) {
		return value as T;
	}
	const [head, ...rest] = path;
	const existing = (obj as Record<string, unknown>)[head];
	return {
		...obj,
		[head]: setPath((existing ?? {}) as object, rest, value)
	};
}
